import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from '../utils/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap select-none',
    'font-medium font-sans rounded-md border border-transparent',
    'transition-[background,transform,box-shadow,border-color,color] duration-fast ease-mela',
    'disabled:opacity-50 disabled:pointer-events-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron-500 focus-visible:ring-offset-2',
    'hover:-translate-y-px hover:shadow-elevated active:translate-y-0 active:shadow-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-saffron-500 text-white hover:bg-saffron-600 hover:shadow-saffron-glow',
        violet: 'bg-violet-600 text-white hover:bg-violet-700 hover:shadow-violet-glow',
        ink: 'bg-ink-900 text-white hover:bg-ink-800',
        outline:
          'bg-transparent border-ink-300 text-ink-900 hover:bg-ink-100 hover:border-ink-400',
        ghost:
          'bg-transparent text-ink-700 hover:bg-ink-100 hover:translate-y-0 hover:shadow-none',
        danger: 'bg-vermilion-500 text-white hover:bg-vermilion-700',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-[38px] px-4 text-[14px]',
        lg: 'h-12 px-6 text-[15px]',
        xl: 'h-14 px-8 text-[16px] font-semibold',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
);
Button.displayName = 'Button';

export interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ className, variant, size, ...props }, ref) => (
    <a
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
);
ButtonLink.displayName = 'ButtonLink';
